import React from 'react';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { DynamicForm } from '@/components/common/DynamicForm';
import { ViewModal } from '@/components/common/ViewModal';
import { abacActionEntity } from '@/config/entities/abacActions';
import { abacActionGenericService } from '@/services/abac';
import { toast } from 'react-hot-toast';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

const ActionsPage: React.FC = () => {
  const [showForm, setShowForm] = React.useState(false);
  const [editingItem, setEditingItem] = React.useState<any>(null);
  const [viewingItem, setViewingItem] = React.useState<any>(null);
  const [showViewModal, setShowViewModal] = React.useState(false);
  const { t } = useLanguage();

  const handleCreate = () => {
    setEditingItem(null);
    setShowForm(true);
  };

  const handleEdit = (item: any) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleView = (item: any) => {
    setViewingItem(item);
    setShowViewModal(true);
  };

  const handleToggleStatus = async (id: string, isActive: boolean) => {
    try {
      await abacActionGenericService.toggleStatus(id, isActive);
      toast.success(t('abac.actionStatusUpdated'));
    } catch (error) {
      toast.error(t('abac.failedToUpdateActionStatus'));
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      if (editingItem) {
        await abacActionGenericService.update(editingItem.id, data);
        toast.success(t('abac.actionUpdated'));
      } else {
        await abacActionGenericService.create(data);
        toast.success(t('abac.actionCreated'));
      }
      setShowForm(false);
      setEditingItem(null);
    } catch (error) {
      toast.error(t('abac.failedToSaveAction'));
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingItem(null);
  };

  const handleViewModalClose = () => {
    setShowViewModal(false);
    setViewingItem(null);
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('abac.actionsManagement')}</h1>
        <p className="text-gray-600 mt-2">
          {t('abac.defineAndManageActions')}
        </p>
      </div>

      {showForm ? (
        <DynamicForm
          entity={abacActionEntity}
          initialData={editingItem}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      ) : (
        <FlexibleCRUDTable
          entity={abacActionEntity}
          service={abacActionGenericService}
          showSearch={true}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={async (item: any) => {
            if (window.confirm(t('abac.actions.confirmDelete', { name: item.action_name }))) {
              try {
                await abacActionGenericService.delete(item.id);
                toast.success(t('abac.actions.deleteSuccess'));
              } catch (error) {
                toast.error(t('abac.actions.deleteError'));
              }
            }
          }}
          onCreate={handleCreate}
          showPagination={true}
          searchPlaceholder={t('abac.actions.searchPlaceholder')}
          customActions={[
            {
              label: t('abac.actions.toggleStatus'),
              icon: ArrowPathIcon,
              onClick: async (item: any) => {
                try {
                  await handleToggleStatus(item.id, item.is_active !== true);
                } catch (error) {
                  console.error('Toggle status error:', error);
                }
              }
            }
          ]}
          onOperationComplete={() => {
            // This will be called after successful operations (create, update, delete)
            // The table will automatically refresh via loadData() in the component
          }}
        />
      )}

      {/* View Modal */}
      <ViewModal
        isOpen={showViewModal}
        onClose={handleViewModalClose}
        title={`View Action: ${viewingItem?.action_name || ''}`}
        data={viewingItem || {}}
        fields={[
          { key: 'action_name', label: 'Action Name', type: 'string' },
          { key: 'action_type', label: 'Action Type', type: 'string' },
          { key: 'description', label: 'Description', type: 'string' },
          { key: 'is_active', label: 'Active', type: 'boolean' },
          { key: 'created_at', label: 'Created At', type: 'datetime' },
          { key: 'updated_at', label: 'Updated At', type: 'datetime' }
        ]}
      />
    </div>
  );
};

export default ActionsPage;
